<template>
  <div class="guide-signing-groups">
    <h2>What are signing groups?</h2>
    <p>
      A signing group is a named pool of email addresses where any member can
      fulfill a signing role. Instead of assigning a specific person to sign a
      document, you assign a group. The first member to act completes the role
      for the entire group.
    </p>
    <p>
      Think of it as a shared inbox for signatures. You address the signing
      responsibility to "Legal Team" rather than "Alice specifically." Whoever
      is available first picks it up and signs. Once one member completes the
      signing fields, the role is fulfilled and other members see it as already
      done.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Why use signing groups?</h2>
    <ul>
      <li>
        <strong>Department approvals.</strong> "Any member of Legal can sign,"
        and whoever is available acts first, without waiting for a specific
        individual.
      </li>
      <li>
        <strong>Team countersigning.</strong> Rotate signing responsibility
        among multiple authorized signers based on availability, without manual
        reassignment.
      </li>
      <li>
        <strong>Delegate scenarios.</strong> Senior staff can designate
        alternates authorized to sign on their behalf without needing to
        reassign the package.
      </li>
      <li>
        <strong>Remove bottlenecks.</strong> Eliminate single-person
        dependencies for signing completion. If one person is on vacation, out
        sick, or simply busy, another group member can step in immediately.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>How groups work</h2>
    <p>The lifecycle of a signing-group interaction follows four steps:</p>

    <h3>Step 1. Create a group</h3>
    <p>
      Define the group with a descriptive name, optional description, and its
      initial members. A group holds up to 100 members. Duplicate email
      addresses within the list are rejected with <code>400 Bad Request</code>.
    </p>
    <GuideCodeTabs :samples="samples.createGroup" />
    <p>
      Requires <code>sign.group.create</code> to create a group,
      <code>sign.group.update</code> to add or remove members, and
      <code>sign.group.delete</code> to remove a group. Listing groups requires
      <code>sign.group.read</code>.
    </p>

    <h3>Step 2. All current members receive notification when the package is sent</h3>
    <p>
      Group membership resolves to a concrete member list at the moment the
      package is sent. Every member in the group at that instant receives the
      signing notification. Each member has equal authority to complete the
      signing role.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="schedule" color="amber-10" />
      </template>
      <strong>Membership resolves at send time, not at signer-add time.</strong>
      Adding or removing members from the group after the package is sent does
      not affect the in-flight package. Its resolved member list is frozen.
      Group edits only influence packages sent after the edit.
    </q-banner>

    <h3>Step 3. First member to complete fulfills the role</h3>
    <p>
      The first member who opens the signing ceremony and completes all
      assigned fields fulfills the signing role for the entire group. This is a
      first-come, first-served model. Duplicate signatures are not possible.
      Once the role is fulfilled, other members see it as already done.
    </p>

    <h3>Step 4. Audit trail records the actual signer</h3>
    <p>
      The audit trail and Certificate of Completion record the specific member
      who completed the role (their email, name, IP, and timestamp), not the
      group's display name. You always have a clean record of who actually
      signed.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Managing signing groups in the portal</h2>
    <p>
      The portal's <strong>Signing Groups</strong> page lets you create,
      rename, and delete groups, and manage each group's membership. Click
      <strong>New Group</strong> to create one, give it a name and
      description, and add members by email. Existing groups show their member
      count. Click into a group to see, add, or remove members.
    </p>
    <p>
      When creating a package in the portal, the signer panel offers a
      "Signing Group" option as an alternative to a specific individual. Pick
      the group and every current member receives the signing notification when
      the package is sent. Whoever acts first fulfills the role for the whole
      group.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Groups are tenant-scoped and reusable across packages. The REST API and MCP tools
      cover group CRUD, member management, and the package-signer link via
      <code>signingGroupId</code>.
    </p>

    <h3>Assigning groups to packages</h3>
    <p>
      When adding a signer to a package, use the <code>signingGroupId</code>
      property to assign a group instead of (or alongside) a specific
      individual email. The email and name fields serve as the display identity
      for the signing role.
    </p>
    <GuideCodeTabs :samples="samples.assignGroupToPackage" />
    <p>
      The <code>signingGroupId</code> links this signer slot to the group. All
      group members receive the notification and can fulfill the role, even
      though the signer record shows the group's display name and email.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Group management</h3>
    <p>
      Groups are living entities. Members can be added or removed as your team
      changes.
    </p>

    <h3>Add members</h3>
    <GuideCodeTabs :samples="samples.addMember" />

    <h3>Remove members</h3>
    <p>
      Email matching is case-insensitive. Members added after a package has
      been sent are not retroactively notified for that package, only for
      future sends.
    </p>
    <GuideCodeTabs :samples="samples.removeMember" />

    <h3>List members</h3>
    <GuideCodeTabs :samples="samples.listMembers" />
    <p>
      Returns the full list of current group members with their email addresses
      and names.
    </p>

    <h3>Rename or update the description</h3>
    <GuideCodeTabs :samples="samples.updateGroup" />

    <h3>Bulk delete</h3>
    <GuideCodeTabs :samples="samples.bulkDelete" />
    <p>
      Up to 50 group GUIDs per request. Each GUID is processed independently.
      The response reports success or failure per item.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Deleting a group</h3>
    <p>
      Deleting a signing group does not affect packages that were already sent.
      Because membership resolves at send time, in-flight packages carry their
      own frozen member list and continue to function normally even if the
      source group is deleted afterwards. Delete freely when the group is no
      longer needed for future packages.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Keep groups small (3 to 10 members) for clarity.</strong> Larger
        groups dilute accountability and make it harder to track who actually
        signed. If you need more than 10 members, consider whether the group
        represents a single signing authority or should be split into sub-teams.
      </li>
      <li>
        <strong>Use descriptive group names that reflect the signing
        authority.</strong> Names like "Board of Directors," "Legal Approvers,"
        or "Finance Team" make it immediately clear what role the group
        represents in audit trails and package history.
      </li>
      <li>
        <strong>Regularly audit group membership.</strong> Remove departed
        employees promptly. A former employee who still appears in a signing
        group could receive signing notifications for documents they should no
        longer access.
      </li>
      <li>
        <strong>Document your group naming conventions.</strong> Consistent
        naming across your organization makes it easier to select the right
        group when creating packages and reduces the risk of creating duplicate
        groups for the same team.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> Signing groups work with both email-based and
      embedded signing flows. When combined with sequential signing order, the
      group's turn in the sequence begins when the previous signer completes,
      and any group member can then fulfill the role.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>SigningGroups</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    // ─── Create a signing group with initial members ────────────────────────────
    createGroup: {
        curl: `curl -X POST ${API}/api/v1/signing-groups \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Legal Team",
    "description": "Any member may sign on behalf of Legal",
    "members": [
      { "email": "alice@company.com", "name": "Alice Chen" },
      { "email": "bob@company.com",   "name": "Bob Martinez" },
      { "email": "carol@company.com", "name": "Carol Davis" }
    ]
  }'`,

        js: `const response = await fetch('${API}/api/v1/signing-groups', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Legal Team',
    description: 'Any member may sign on behalf of Legal',
    members: [
      { email: 'alice@company.com', name: 'Alice Chen' },
      { email: 'bob@company.com',   name: 'Bob Martinez' },
      { email: 'carol@company.com', name: 'Carol Davis' }
    ]
  })
});
const group = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/signing-groups', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Legal Team',
    description: 'Any member may sign on behalf of Legal',
    members: [
      { email: 'alice@company.com', name: 'Alice Chen' },
      { email: 'bob@company.com',   name: 'Bob Martinez' },
      { email: 'carol@company.com', name: 'Carol Davis' }
    ]
  })
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const group = await response.json();
console.log('Created group', group.id);`,

        python: `import os, requests

response = requests.post(
    '${API}/api/v1/signing-groups',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Legal Team',
        'description': 'Any member may sign on behalf of Legal',
        'members': [
            {'email': 'alice@company.com', 'name': 'Alice Chen'},
            {'email': 'bob@company.com',   'name': 'Bob Martinez'},
            {'email': 'carol@company.com', 'name': 'Carol Davis'},
        ]
    }
)
response.raise_for_status()
group = response.json()`,

        csharp: `var payload = new
{
    name = "Legal Team",
    description = "Any member may sign on behalf of Legal",
    members = new[]
    {
        new { email = "alice@company.com", name = "Alice Chen" },
        new { email = "bob@company.com",   name = "Bob Martinez" },
        new { email = "carol@company.com", name = "Carol Davis" },
    }
};

var response = await client.PostAsJsonAsync("api/v1/signing-groups", payload);
response.EnsureSuccessStatusCode();
var group = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/signing-groups', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Legal Team',
        'description' => 'Any member may sign on behalf of Legal',
        'members' => [
            ['email' => 'alice@company.com', 'name' => 'Alice Chen'],
            ['email' => 'bob@company.com',   'name' => 'Bob Martinez'],
            ['email' => 'carol@company.com', 'name' => 'Carol Davis'],
        ],
    ],
]);
$group = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/signing-groups')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Legal Team',
  description: 'Any member may sign on behalf of Legal',
  members: [
    { email: 'alice@company.com', name: 'Alice Chen' },
    { email: 'bob@company.com',   name: 'Bob Martinez' },
    { email: 'carol@company.com', name: 'Carol Davis' }
  ]
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
group = JSON.parse(res.body)`
    },

    // ─── Assign a group to a signer slot on a package ───────────────────────────
    assignGroupToPackage: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "legal@company.com",
    "name":  "Legal Team",
    "signingGroupId": "group-guid-here"
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'legal@company.com',
    name:  'Legal Team',
    signingGroupId: groupId
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'legal@company.com',
    name:  'Legal Team',
    signingGroupId: groupId
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'email': 'legal@company.com',
        'name':  'Legal Team',
        'signingGroupId': group_id
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync($"api/v1/packages/{packageId}/signers", new
{
    email = "legal@company.com",
    name  = "Legal Team",
    signingGroupId = groupId
});`,

        php: `$client->post("api/v1/packages/{$packageId}/signers", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'email' => 'legal@company.com',
        'name'  => 'Legal Team',
        'signingGroupId' => $groupId,
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  email: 'legal@company.com',
  name:  'Legal Team',
  signingGroupId: group_id
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Add a member to an existing group ──────────────────────────────────────
    addMember: {
        curl: `curl -X POST ${API}/api/v1/signing-groups/$GROUP_ID/members \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "email": "dave@company.com", "name": "Dave Wilson" }'`,

        js: `await fetch(\`${API}/api/v1/signing-groups/\${groupId}/members\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ email: 'dave@company.com', name: 'Dave Wilson' })
});`,

        node: `await fetch(\`${API}/api/v1/signing-groups/\${groupId}/members\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ email: 'dave@company.com', name: 'Dave Wilson' })
});`,

        python: `requests.post(
    f'${API}/api/v1/signing-groups/{group_id}/members',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'email': 'dave@company.com', 'name': 'Dave Wilson'}
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/signing-groups/{groupId}/members",
    new { email = "dave@company.com", name = "Dave Wilson" });`,

        php: `$client->post("api/v1/signing-groups/{$groupId}/members", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['email' => 'dave@company.com', 'name' => 'Dave Wilson'],
]);`,

        ruby: `uri = URI("${API}/api/v1/signing-groups/#{group_id}/members")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { email: 'dave@company.com', name: 'Dave Wilson' }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Remove a member (by email; case-insensitive) ───────────────────────────
    removeMember: {
        curl: `curl -X DELETE ${API}/api/v1/signing-groups/$GROUP_ID/members/dave@company.com \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `await fetch(
  \`${API}/api/v1/signing-groups/\${groupId}/members/\${encodeURIComponent(email)}\`,
  { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);`,

        node: `const url = \`${API}/api/v1/signing-groups/\${groupId}/members/\${encodeURIComponent(email)}\`;
const response = await fetch(url, {
  method: 'DELETE',
  headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN }
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);`,

        python: `from urllib.parse import quote

response = requests.delete(
    f'${API}/api/v1/signing-groups/{group_id}/members/{quote(email)}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()`,

        csharp: `var response = await client.DeleteAsync(
    $"api/v1/signing-groups/{groupId}/members/{Uri.EscapeDataString(email)}");
response.EnsureSuccessStatusCode();`,

        php: `$client->delete(
    "api/v1/signing-groups/{$groupId}/members/" . rawurlencode($email),
    ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]]
);`,

        ruby: `uri = URI("${API}/api/v1/signing-groups/#{group_id}/members/#{URI.encode_www_form_component(email)}")
req = Net::HTTP::Delete.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── List current members of a group ────────────────────────────────────────
    listMembers: {
        curl: `curl ${API}/api/v1/signing-groups/$GROUP_ID/members \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/signing-groups/\${groupId}/members\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const members = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/signing-groups/\${groupId}/members\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const members = await response.json();`,

        python: `response = requests.get(
    f'${API}/api/v1/signing-groups/{group_id}/members',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
members = response.json()`,

        csharp: `var members = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/signing-groups/{groupId}/members");`,

        php: `$response = $client->get("api/v1/signing-groups/{$groupId}/members", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$members = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/signing-groups/#{group_id}/members")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
members = JSON.parse(res.body)`
    },

    // ─── Rename / update description ────────────────────────────────────────────
    updateGroup: {
        curl: `curl -X PUT ${API}/api/v1/signing-groups/$GROUP_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "Legal Team - Corporate" }'`,

        js: `await fetch(\`${API}/api/v1/signing-groups/\${groupId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ name: 'Legal Team - Corporate' })
});`,

        node: `await fetch(\`${API}/api/v1/signing-groups/\${groupId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ name: 'Legal Team - Corporate' })
});`,

        python: `requests.put(
    f'${API}/api/v1/signing-groups/{group_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'name': 'Legal Team - Corporate'}
).raise_for_status()`,

        csharp: `var response = await client.PutAsJsonAsync(
    $"api/v1/signing-groups/{groupId}",
    new { name = "Legal Team - Corporate" });
response.EnsureSuccessStatusCode();`,

        php: `$client->put("api/v1/signing-groups/{$groupId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['name' => 'Legal Team - Corporate'],
]);`,

        ruby: `uri = URI("${API}/api/v1/signing-groups/#{group_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { name: 'Legal Team - Corporate' }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Bulk delete (up to 50 per request, per-item results) ───────────────────
    bulkDelete: {
        curl: `curl -X POST ${API}/api/v1/signing-groups/bulk-delete \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "ids": ["group-guid-1", "group-guid-2"] }'`,

        js: `const response = await fetch('${API}/api/v1/signing-groups/bulk-delete', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ids: ['group-guid-1', 'group-guid-2'] })
});
const { results } = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/signing-groups/bulk-delete', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ ids: ['group-guid-1', 'group-guid-2'] })
});
const { results } = await response.json();
results.filter(r => !r.success).forEach(r => console.error('Failed:', r.id, r.error));`,

        python: `response = requests.post(
    '${API}/api/v1/signing-groups/bulk-delete',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'ids': ['group-guid-1', 'group-guid-2']}
)
response.raise_for_status()
for item in response.json()['results']:
    if not item['success']:
        print('Failed:', item['id'], item['error'])`,

        csharp: `var payload = new { ids = new[] { "group-guid-1", "group-guid-2" } };
var response = await client.PostAsJsonAsync("api/v1/signing-groups/bulk-delete", payload);
response.EnsureSuccessStatusCode();
var body = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/signing-groups/bulk-delete', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['ids' => ['group-guid-1', 'group-guid-2']],
]);
$results = json_decode($response->getBody(), true)['results'];`,

        ruby: `uri = URI('${API}/api/v1/signing-groups/bulk-delete')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { ids: ['group-guid-1', 'group-guid-2'] }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
results = JSON.parse(res.body)['results']`
    }
};

export default {
    name: 'GuideSigningGroups',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
